import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { formatPrice, MIN_PASSWORD_LENGTH, type Locale } from '@diana/shared'
import { api } from '../../lib/api.js'
import { authClient, signOutAndLeave } from '../../lib/auth.js'
import { useLocale } from '../../i18n/index.js'
import { Column, Main } from '../../classroom/Shell.js'
import { Button, ErrorLine, FieldLabel, Rows, RowText, SectionHead, Skeleton, Title } from '../../classroom/ui.js'
import { studentCopy } from './copy.js'
import { shellCopy } from './copyShell.js'

/**
 * Konto (Student-Account.png): name, the language explanations come in
 * (users.locale), the password, and the lessons bought from Diana. One
 * „Zapisz zmiany” saves whatever changed; a new language reloads the panel
 * in that language.
 */
export function StudentAccountPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const a = shellCopy(locale).account
  const { data, isPending, isError } = useQuery({ queryKey: ['student', 'me'], queryFn: api.student.me })

  const [name, setName] = useState('')
  const [lang, setLang] = useState<Locale>(locale)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (data) setName(data.profile.name)
  }, [data])

  const dirty = Boolean(data) && (name.trim() !== data?.profile.name || lang !== locale || (current && next))

  async function save() {
    if (!data) return
    setBusy(true)
    setMsg(null)
    try {
      if (current && next) {
        const { error } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: true })
        if (error) {
          setMsg({ ok: false, text: a.wrongPassword })
          return
        }
        setCurrent('')
        setNext('')
      }
      const changes: { name?: string; locale?: Locale } = {}
      if (name.trim() && name.trim() !== data.profile.name) changes.name = name.trim()
      if (lang !== locale) changes.locale = lang
      if (Object.keys(changes).length > 0) await api.student.update(changes)
      // The whole panel speaks the account's language: load it afresh in the new one.
      if (changes.locale || changes.name) {
        window.location.reload()
        return
      }
      setMsg({ ok: true, text: a.saved })
    } catch {
      setMsg({ ok: false, text: t.errors.load })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Column
      top={<p className="m-0 font-semibold">{t.nav.account}</p>}
      bottom={
        <>
          <button
            type="button"
            className="cl-link-quiet"
            onClick={() => void signOutAndLeave()}
          >
            {a.signOut}
          </button>
          {msg && (
            <span role="status" className={`text-class-gloss font-semibold ${msg.ok ? 'text-class-correct' : 'text-class-wrong'}`}>
              {msg.ok ? '✓' : '✗'} {msg.text}
            </span>
          )}
          <Button className="ml-auto" disabled={!dirty || busy} onClick={save}>
            {busy ? a.saving : a.save}
          </Button>
        </>
      }
    >
      <Main width={820}>
        <Title sub={a.sub}>{t.nav.account}</Title>
        {isPending ? (
          <Skeleton lines={5} />
        ) : isError || !data ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : (
          <>
            <section className="flex flex-col">
              <SectionHead>{a.data}</SectionHead>
              <div className="grid gap-5 desk:grid-cols-2">
                <div>
                  <FieldLabel htmlFor="acc-name">{a.name}</FieldLabel>
                  <input id="acc-name" className="cl-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <FieldLabel htmlFor="acc-email">{a.email}</FieldLabel>
                  <input id="acc-email" className="cl-input" value={data.profile.email} disabled />
                  <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">{a.emailHint}</p>
                </div>
              </div>
            </section>

            <section className="flex flex-col">
              <SectionHead>{a.language}</SectionHead>
              <p className="m-0 mb-3 text-class-gloss text-class-ink-soft">{a.languageHint}</p>
              <Rows>
                {a.languages.map((l) => (
                  <label key={l.code} className="flex min-h-[60px] cursor-pointer items-center gap-4 px-1 py-2">
                    <input
                      type="radio"
                      name="lang"
                      value={l.code}
                      checked={lang === l.code}
                      onChange={() => setLang(l.code)}
                      className="h-[18px] w-[18px] accent-[var(--color-class-sky-700)]"
                    />
                    <RowText title={l.name} meta={l.hint} />
                    <span className="text-class-label font-semibold text-class-ink-faint">{l.code === 'uk' ? 'UA' : l.code.toUpperCase()}</span>
                  </label>
                ))}
              </Rows>
            </section>

            <section className="flex flex-col">
              <SectionHead>{a.password}</SectionHead>
              <div className="grid gap-5 desk:grid-cols-2">
                <div>
                  <FieldLabel htmlFor="acc-cur">{a.current}</FieldLabel>
                  <input
                    id="acc-cur"
                    type="password"
                    className="cl-input"
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="acc-new">{a.next}</FieldLabel>
                  <input
                    id="acc-new"
                    type="password"
                    className="cl-input"
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                  />
                </div>
              </div>
              <p className="m-0 mt-2.5 text-class-gloss text-class-ink-faint">{a.passwordHint}</p>
            </section>

            <section className="flex flex-col">
              <SectionHead>{a.purchases}</SectionHead>
              {data.purchases.length === 0 ? (
                <p className="m-0 text-class-ink-soft">{a.noPurchases}</p>
              ) : (
                <Rows>
                  {data.purchases.map((p) => (
                    <div key={p.id} className="flex min-h-16 items-center gap-6 px-1 py-2">
                      <RowText
                        title={a.purchase(p.lessons)}
                        meta={a.bought(DateTime.fromISO(p.purchasedAt).setLocale(locale).toFormat('d MMMM yyyy'))}
                      />
                      <span className="font-semibold">{formatPrice(p.totalGrosz)}</span>
                    </div>
                  ))}
                </Rows>
              )}
              <Link to="/panel/lekcje" className="mt-3.5 self-start font-semibold">
                {a.seeLessons} ›
              </Link>
            </section>
          </>
        )}
      </Main>
    </Column>
  )
}
